import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "@/hooks/use-toast";
import { api, edenFetch } from "@/lib/eden";
import { AUTH_ME_QUERY_KEY, queryClient } from "@/lib/queryClient";

/**
 * Member-initiated account joining (PRD 08 D21), web side.
 *
 * `adopt_new` — "Do you already have an account?" on the social sign-up step: the new
 * (empty) account is folded into the one the member signs in to next, so e.g. their
 * Apple "Hide My Email" sign-in lands on their real account from then on.
 * `merge_into` — Email Settings "Merge another account": the account that starts it
 * keeps everything; the one signed in to next closes into it.
 *
 * The server holds the claim (httpOnly cookie → Redis); this module only remembers
 * WHICH flow is in progress (sessionStorage) so the login page and the root finisher
 * can say the right thing.
 */
export type ClaimKind = "adopt_new" | "merge_into";

const MARKER = "bday_account_claim";

export function readClaimMarker(): ClaimKind | null {
  try {
    const v = window.sessionStorage.getItem(MARKER);
    return v === "adopt_new" || v === "merge_into" ? v : null;
  } catch {
    return null;
  }
}

function setClaimMarker(kind: ClaimKind | null): void {
  try {
    if (kind) window.sessionStorage.setItem(MARKER, kind);
    else window.sessionStorage.removeItem(MARKER);
  } catch {
    /* private mode — the server-side claim still works, only the copy is generic */
  }
}

/** Start a claim on the CURRENT account, then go sign in to the other one. */
export async function startAccountClaim(kind: ClaimKind): Promise<void> {
  await edenFetch(api.api.user.me.claim.start.post({ kind }));
  setClaimMarker(kind);
  // The server just ended this session; a hard navigation drops every cached query.
  window.location.href = `/login?claim=${kind === "adopt_new" ? "existing" : "merge"}`;
}

/** The note shown above the sign-in options while a claim is waiting. */
export function AccountClaimBanner() {
  const [kind] = useState(readClaimMarker);
  if (!kind) return null;
  return (
    <div
      role="status"
      className="w-full max-w-[450px] rounded-md border border-primary/40 bg-primary/5 p-4 text-[15px]"
    >
      {kind === "adopt_new" ? (
        <>
          <b>Sign in to the BirthdayAlarm account you already have</b> — with the email and
          password, Google or Facebook you used before. We'll connect your new sign-in to it so it
          takes you straight there next time.
        </>
      ) : (
        <>
          <b>Now sign in to your OTHER BirthdayAlarm account</b> — the one you want to merge and
          close. You'll see what moves before anything happens.
        </>
      )}
      <button
        type="button"
        className="ml-1 cursor-pointer text-primary underline"
        onClick={() => {
          setClaimMarker(null);
          window.location.reload();
        }}
      >
        Cancel
      </button>
    </div>
  );
}

interface Preview {
  kind: ClaimKind;
  closing: {
    display_name: string | null;
    email: string | null;
    providers: string[];
    contacts: number;
    cards: number;
  };
  keeping: { display_name: string | null; email: string | null };
  providers_not_moving: string[];
  blocked: string | null;
}

const LABEL: Record<string, string> = { google: "Google", apple: "Apple", facebook: "Facebook" };
const list = (ps: string[]) => ps.map((p) => LABEL[p] ?? p).join(" and ");

/**
 * Root-mounted: once the member is signed in to the second account, finish (adopt_new)
 * or confirm-then-finish (merge_into) the claim. Holds off while the D19 "Almost there"
 * gate is up — that means they signed in to a brand-new account again.
 */
export function AccountClaimFinisher() {
  const { user } = useAuth();
  const flags = user as unknown as {
    signup_details_required?: boolean;
    impersonation?: unknown;
  } | null;
  const [kind, setKind] = useState(readClaimMarker);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const active = !!kind && !!user && !flags?.signup_details_required && !flags?.impersonation;

  const preview = useQuery({
    queryKey: ["account-claim-preview", user?.user_id, kind],
    enabled: active,
    retry: false,
    queryFn: async () => (await edenFetch(api.api.user.me.claim.preview.post({}))) as Preview,
  });

  const finish = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = (await edenFetch(api.api.user.me.claim.complete.post({}))) as {
        kind: ClaimKind;
        providers_moved: string[];
      };
      setClaimMarker(null);
      setKind(null);
      toast({
        variant: "success",
        description:
          res.kind === "adopt_new"
            ? res.providers_moved.length
              ? `Done — ${list(res.providers_moved)} sign-in now takes you to this account.`
              : "Done — your accounts are joined."
            : "Done — your accounts are merged into this one.",
      });
      if (res.kind === "merge_into") {
        // The session now belongs to the surviving account.
        window.location.href = "/email_settings";
        return;
      }
      void queryClient.invalidateQueries({ queryKey: AUTH_ME_QUERY_KEY });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  // adopt_new needs no confirmation: the account closing is brand-new and empty.
  const p = preview.data;
  useEffect(() => {
    if (p && p.kind === "adopt_new" && !p.blocked && !busy && !error) void finish();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once per preview
  }, [p]);

  const abandon = async () => {
    try {
      await edenFetch(api.api.user.me.claim.cancel.post({}));
    } catch {
      /* expired already */
    }
    setClaimMarker(null);
    setKind(null);
  };

  if (!active) return null;
  const problem = error ?? p?.blocked ?? (preview.error ? preview.error.message : null);
  if (!problem && (!p || p.kind === "adopt_new")) return null; // loading / auto-finishing

  return (
    <Dialog open>
      <DialogContent showCloseButton={false} className="max-w-[460px]">
        {problem ? (
          <>
            <DialogTitle className="text-center font-serif text-[1.6rem] font-light">
              We couldn't join your accounts
            </DialogTitle>
            <DialogDescription className="text-center text-[16px] text-foreground">
              {problem}
            </DialogDescription>
            <Button className="mt-4" variant="outline" onClick={() => void abandon()}>
              OK
            </Button>
          </>
        ) : (
          p && (
            <>
              <DialogTitle className="text-center font-serif text-[1.6rem] font-light">
                Merge these accounts?
              </DialogTitle>
              <DialogDescription className="text-[16px] text-foreground">
                The account you just signed in to
                {p.closing.email ? ` (${p.closing.email})` : ""} will be <b>closed</b>. Its{" "}
                {p.closing.contacts} contact{p.closing.contacts === 1 ? "" : "s"} and{" "}
                {p.closing.cards} card{p.closing.cards === 1 ? "" : "s"}
                {p.closing.providers.length
                  ? `, and its ${list(p.closing.providers)} sign-in`
                  : ""}{" "}
                move to <b>{p.keeping.email ?? p.keeping.display_name ?? "your other account"}</b>,
                and you'll be signed in there. This can't be undone.
                {p.providers_not_moving.length > 0 &&
                  ` (${list(p.providers_not_moving)} sign-in stays behind — that account already has its own.)`}
              </DialogDescription>
              <div className="mt-4 flex flex-col gap-2">
                <Button size="cta" onClick={() => void finish()} disabled={busy}>
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Merge accounts
                </Button>
                <Button variant="outline" onClick={() => void abandon()} disabled={busy}>
                  Cancel
                </Button>
              </div>
            </>
          )
        )}
      </DialogContent>
    </Dialog>
  );
}
